'use client';

import { isRejectedByMathlive } from '@magicschool/spellbook/lib/latex';
import {
  configureMathfieldAccessibility,
  configureMathfieldMenu,
  describeMathExpression,
  importMathlive,
  markMathfieldInvalid,
} from '@magicschool/spellbook/lib/mathlive';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type { MathfieldElement } from 'mathlive';
import {
  type FocusEventHandler,
  forwardRef,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

const mathFieldVariants = cva(
  [
    'm-0',
    'p-0',
    'inline-block',
    'text-default-text',
    'rounded-sm',
    'align-middle',
    'transition-all',
    'bg-transparent',
    'outline-0',
    'max-w-full',
  ],
  {
    variants: {
      readOnly: {
        false: 'hover:bg-surface-transparent-1 focus-within:bg-surface-transparent-1 focus-within-ring focus-within:!ring-offset-0',
        true: '',
      },
    },
    defaultVariants: {
      readOnly: false,
    },
  },
);

const mathFieldPlaceholderVariants = cva([
  'm-0',
  'p-0',
  'inline-block',
  'rounded-sm',
  'align-middle',
  'bg-transparent',
  'outline-0',
  'max-w-full',
  'min-w-[2rem]',
  'min-h-[1.5rem]',
  'invisible',
]);

/** The element `EditableMath` forwards its ref to. */
export type EditableMathElement = MathfieldElement;

export type EditableMathProps = Omit<React.HTMLAttributes<MathfieldElement>, 'onChange' | 'defaultValue' | 'value' | 'read-only'> & {
  /** Initial LaTeX value (uncontrolled) */
  defaultValue?: string;
  /** Current LaTeX value (controlled) */
  value?: string;
  /** Called on the field's `input` events; MathLive fires none for a programmatic `value` set. */
  onValueChange?: (value: string) => void;
  /** Read-only mode for displaying math without editing */
  readOnly?: boolean;
  /** Disabled state for the math field */
  disabled?: boolean;
  /** Invalid state for the math field. LaTeX MathLive cannot parse is marked invalid automatically. */
  invalid?: boolean;
  /**
   * Show virtual keyboard
   * @note Virtual keyboard does not work inside iframes (e.g., Storybook).
   * It will work in standalone pages and production apps, I hope.
   */
  showVirtualKeyboard?: boolean;
  /** Auto-focus the field when mounted */
  autoFocus?: boolean;
  /** Callback when blur event occurs */
  onBlur?: FocusEventHandler<MathfieldElement>;
  /** Callback when focus event occurs */
  onFocus?: FocusEventHandler<MathfieldElement>;
  /** Callback when key down event occurs */
  onKeyDown?: KeyboardEventHandler<MathfieldElement>;
  /** Callback when click event occurs */
  onClick?: MouseEventHandler<MathfieldElement>;
  /** Callback when mouse down event occurs */
  onMouseDown?: MouseEventHandler<MathfieldElement>;
};

/**
 * Component for editing LaTeX mathematical expressions.
 * Simplified to work as a transparent wrapper around math-field for use mostly in BlockNote specs.
 * A value MathLive cannot parse is still loaded as given and marked invalid, never cleared, so a
 * teacher can still see and fix it. Callers that commit on close should go through `useMathFieldCommit`.
 *
 * It is our hope that this component can merge with the `Math` component in the future.
 *
 * ### When to use
 * - Editing a LaTeX expression inline, primarily inside BlockNote editor specs
 *
 * ### When NOT to use
 * - Displaying a finished expression: use `Math`
 * - Editing ordinary text: use `EditableText`
 */
export const EditableMath = forwardRef<MathfieldElement, EditableMathProps>(
  (
    {
      defaultValue,
      value,
      onValueChange,
      className,
      'aria-label': ariaLabel,
      readOnly = false,
      disabled = false,
      invalid = false,
      showVirtualKeyboard = true,
      autoFocus = false,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<MathfieldElement>(null);
    const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue);
    const [isReady, setIsReady] = useState(false);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const isInteractive = !readOnly && !disabled;

    useImperativeHandle(forwardedRef, () => ref.current!);

    useEffect(() => {
      let mounted = true;
      importMathlive().then(() => {
        if (mounted) setIsReady(true);
      });
      return () => {
        mounted = false;
      };
    }, []);

    useEffect(() => {
      if (!isReady || !ref.current) return;
      ref.current.value = currentValue ?? '';
    }, [isReady, currentValue]);

    // Configure field
    useEffect(() => {
      const field = ref.current;
      if (!isReady || !field) return;

      importMathlive().then(async (mathlive) => {
        if (!field || !ref.current) return;

        await customElements.whenDefined('math-field');

        field.menuItems = configureMathfieldMenu(field.menuItems);
        field.placeholder = '';
        field.readOnly = !isInteractive;

        const expression = currentValue ?? '';
        configureMathfieldAccessibility(field, {
          label: ariaLabel ?? describeMathExpression(expression, mathlive),
          readOnly,
          disabled,
          invalid: invalid || isRejectedByMathlive(expression, mathlive),
        });
      });
    }, [isReady, currentValue, ariaLabel, isInteractive, readOnly, disabled, invalid]);

    // Auto-focus when mounted
    useEffect(() => {
      if (autoFocus && isReady && ref.current && isInteractive) {
        setTimeout(() => {
          ref.current?.focus();
        }, 0);
      }
    }, [autoFocus, isReady, isInteractive]);

    // Handle input changes
    useEffect(() => {
      const field = ref.current;
      if (!field || !isReady) return;

      const handleInput = (e: Event) => {
        e.stopPropagation();

        const newValue = field.value;
        if (!isControlled) {
          setInternalValue(newValue);
        }
        onValueChange?.(newValue);
        importMathlive().then((mathlive) => markMathfieldInvalid(field, invalid || isRejectedByMathlive(newValue, mathlive)));
      };

      field.addEventListener('input', handleInput);
      return () => field.removeEventListener('input', handleInput);
    }, [isReady, isControlled, onValueChange, invalid]);

    if (!isReady) {
      // Render invisible placeholder to maintain layout space during MathLive import
      // This prevents flicker by avoiding DOM removal/insertion cycle
      return <span data-spellbook-name="editable-math" className={cn(mathFieldPlaceholderVariants(), className)} aria-hidden="true" />;
    }

    return (
      <math-field
        data-spellbook-name="editable-math"
        ref={ref}
        default-mode="math"
        show-virtual-keyboard={showVirtualKeyboard && isInteractive ? 'on-focus' : 'off'}
        className={cn(mathFieldVariants({ readOnly: !isInteractive }), className)}
        {...props}
      >
        {currentValue || ''}
      </math-field>
    );
  },
);
